import React, { useMemo, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';
import InputText from 'relements/build/esm/input/text';
import Dialog from 'relements/build/esm/overlays/dialog';
import Avatar from 'relements/build/esm/ui/avatar';

import BreadCrumb from '@src/components/BreadCrumb/BreadCrumb';
import Tick from '@src/containers/AgentEscalation/assets/tick_white.svg';
import { useInitials } from '@src/hooks';
import BotStore from '@src/store';
import { Container, SvgIcon, CustomButton } from './styledComponents';
import ZohoSetup from './Zoho/ZohoSetup';
import { createBot } from './Zoho/actions';
import { ZOHO_FQDN } from '../../const';

const Setup = ({
  isActiveToolExist,
  toolName,
  iconUrl,
  callbackURL,
  callbackUrlDomain,
  fields,
  navToHome,
  saveConfiguration,
  deactivateConfiguration,
  fqdn,
  zohoAuthResponse,
}) => {
  const form = useMemo(
    () =>
      fields?.reduce(
        (acc, field) => ({
          ...acc,
          /*  
            if initially all fields are empty and value key doesn't exist then form will take value undefined 
            and updatedForm will have value for field is empty string
            at the time of comparing it'll say fields are updated bcz (undefined != '') but it's not updated 
          */
          [`${field.name}`]: field.value || '',
        }),
        {}
      ),
    [fields]
  );

  const [updated, setUpdated] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [updatedForm, setUpdatedForm] = useState({ ...form });
  const [isAllRequiredFilled, setIsAllRequiredFilled] = useState(false);
  const [unsavedDialogActive, setUnsavedDialogActive] = useState(false);
  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [routeToZoho, setRouteToZoho] = useState(true);
  const [zohoProps, setZohoProps] = useState({});
  const isProductionEnv = BotStore.isProductionEnv();
  let zohoSalesIQUrl = '';
  if (fields.length) {
    let screenName = '';
    const botId =
      fields.filter((field) => field.key === 'bot_id')[0]?.value || '';
    if (zohoAuthResponse?.is_auth && zohoAuthResponse?.screen_name) {
      screenName = zohoAuthResponse?.screen_name;
    } else {
      screenName =
        fields.filter((field) => field.key === 'screen_name')[0]?.value || '';
    }

    zohoSalesIQUrl = `https://salesiq.zoho.com/${screenName}/settings/bot/zobot/${botId}/`;
  }

  const handleFormChange = useCallback(
    (field) =>
      debounce((value) => {
        setUpdatedForm((updatedForm) => {
          const tempFields = {
            ...updatedForm,
            [field]: value,
          };

          setUpdated(() => {
            let flag = false;
            Object.keys(tempFields).forEach((key) => {
              if (tempFields[key] !== form[key]) flag = true;
            });
            return flag;
          });

          setIsAllRequiredFilled(() =>
            fields.reduce(
              (acc, field) =>
                acc && (!field.required || tempFields[field.name]),
              true
            )
          );

          return tempFields;
        });
      }, 200),
    [form, fields]
  );

  const handleSaveConfiguration = async () => {
    const updatedFormDetails = updatedForm;
    if (fqdn === ZOHO_FQDN) {
      if (!isActiveToolExist) {
        const {
          response: { body: response },
        } = await createBot(
          zohoProps.selectedBrand,
          zohoProps.selectedDept,
          zohoProps.selectedPortal,
          callbackUrlDomain + callbackURL
        );
        updatedFormDetails.bot_id = response.data.id;
        if (!isProductionEnv) {
          updatedFormDetails.screen_name = zohoProps.selectedPortal;
          updatedFormDetails.app_id = zohoProps.selectedBrand;
          updatedFormDetails.department_ids = zohoProps.selectedDept.join(',');
        }
      } else {
        const screenName =
          fields.filter((field) => field.key === 'screen_name')[0]?.value || '';
        const botId =
          fields.filter((field) => field.key === 'bot_id')[0]?.value || '';
        updatedFormDetails.screen_name = screenName;
        updatedFormDetails.bot_id = botId;
      }
      if (updatedFormDetails.undefined) delete updatedFormDetails.undefined;
    }
    saveConfiguration(updatedFormDetails, form, callbackURL, () => {
      // these states should come from central state management
      setIsSaved(false);
      setUpdated(true);
    });
    // optimistic UI update
    setIsSaved(true);
    setUpdated(false);
    setUnsavedDialogActive(false);
  };

  const activateUnsavedDialog = useCallback(() => {
    setUnsavedDialogActive(true);
  }, []);

  const deactivateUnsavedDialog = useCallback(() => {
    setUnsavedDialogActive(false);
  }, []);

  const activateDeleteDialog = useCallback(() => {
    setDeleteDialogActive(true);
  }, []);

  const deactivateDeleteDialog = useCallback(() => {
    setDeleteDialogActive(false);
  }, []);

  const handleDeactivateConfiguration = useCallback(() => {
    deactivateConfiguration(
      () => {},
      () => {
        navToHome();
      }
    );

    setDeleteDialogActive(false);
  }, [deactivateConfiguration, navToHome]);

  const handlePathChange = useCallback(() => {
    if (updated) activateUnsavedDialog();
    else navToHome();
  }, [updated, activateUnsavedDialog, navToHome]);

  const nextPage = `${isActiveToolExist ? 'Edit' : 'Configure'} ${toolName}`;

  let saveButtonVariant = 'disabled';
  if (updated && isAllRequiredFilled) saveButtonVariant = 'primary';
  else if (isSaved && !updated) saveButtonVariant = 'success';

  const initials = useInitials(toolName);

  const showZoho = () =>
    routeToZoho &&
    fqdn === ZOHO_FQDN &&
    !isActiveToolExist &&
    !(isProductionEnv && zohoAuthResponse?.is_auth);

  if (showZoho()) {
    return (
      <ZohoSetup
        setRouteToZoho={setRouteToZoho}
        navToHome={navToHome}
        setZohoProps={setZohoProps}
        zohoAuthResponse={zohoAuthResponse}
        showProductionError={isProductionEnv && zohoAuthResponse?.url}
      />
    );
  }

  return (
    <>
      <Container>
        <BreadCrumb
          home="Agent Escalation"
          nextPage={nextPage}
          marginBottom="16px"
          onClick={handlePathChange}
        />
        <div style={{ borderTop: '1px solid #D5D5D5' }} />
        <Box
          marginTop="40px"
          marginBottom="16px"
          padding="zero"
          alignItems="center"
        >
          <Avatar url={iconUrl} initials={initials} marginRight="md" />
          <Text variant="display-2.semi-bold">{nextPage}</Text>
        </Box>
        {!isActiveToolExist && (
          <Text variants="body.md" color="haptik.black">
            Fill in the below details to start configuring&nbsp;
            {toolName}
          </Text>
        )}

        {/* 
          This is calculated based on the current figma design 
          window.innerWidth / 2.91 
        */}
        <Box
          flexDirection="column"
          width={window.innerWidth / 2.91}
          padding="zero"
          marginTop="18px"
        >
          <InputText
            themed
            label="Callback URL"
            style={{ marginBottom: '26px' }}
            value={callbackUrlDomain + callbackURL}
            disabled
          />
          {fields
            .filter((field) => !field.hiddenField)
            .map((field) => (
              <InputText
                key={field.name}
                style={{ marginBottom: '26px' }}
                themed
                label={`${field.required ? '*' : ''} ${field.label}`}
                onChange={handleFormChange(field.name)}
                value={updatedForm[field.name]}
              />
            ))}
          {isActiveToolExist && fqdn === ZOHO_FQDN && zohoSalesIQUrl && (
            <Box padding={0} pb={8}>
              <Text variant="h4.semi-bold">
                Click here for{' '}
                <a
                  href={zohoSalesIQUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Zoho Sales IQ
                </a>
              </Text>
            </Box>
          )}
          <Box
            padding="zero"
            justifyContent={isActiveToolExist ? 'flex-start' : 'flex-end'}
            flex={1}
          >
            {isActiveToolExist && [
              <CustomButton
                themed
                variant="danger"
                onClick={activateDeleteDialog}
                marginRight="auto"
              >
                Delete&nbsp;
                {toolName}
              </CustomButton>,
              <CustomButton
                themed
                variant="secondary"
                onClick={handlePathChange}
                marginRight="21px"
              >
                Cancel
              </CustomButton>,
            ]}
            <CustomButton
              onClick={handleSaveConfiguration}
              themed
              variant={saveButtonVariant}
              width="180px"
              disabled={saveButtonVariant === 'disabled'}
            >
              {isSaved && !updated ? (
                <>
                  <SvgIcon src={Tick} height="11px" width="14px" />
                  &nbsp;&nbsp;Saved
                </>
              ) : (
                'Save Configuration'
              )}
            </CustomButton>
          </Box>
        </Box>
      </Container>
      <Dialog
        onClose={deactivateUnsavedDialog}
        active={unsavedDialogActive}
        title="Unsaved Changes"
        subtitle="You are about to leave this page without saving. All the edits you have made will be lost. Do you really want to leave without saving?"
        primaryAction={handleSaveConfiguration}
        primaryActionText="Save changes"
        secondaryAction={navToHome}
        secondaryActionText="Leaving without saving"
      />
      <Dialog
        onClose={deactivateDeleteDialog}
        active={deleteDialogActive}
        title={`Delete ${toolName}?`}
        subtitle={`Are you sure you want to deactivate ${toolName}? This will delete all the data you have entered`}
        primaryAction={handleDeactivateConfiguration}
      />
    </>
  );
};

Setup.propTypes = {
  isActiveToolExist: PropTypes.bool,
  toolName: PropTypes.string,
  iconUrl: PropTypes.string,
  callbackURL: PropTypes.string.isRequired,
  callbackUrlDomain: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  navToHome: PropTypes.func.isRequired,
  saveConfiguration: PropTypes.func.isRequired,
  deactivateConfiguration: PropTypes.func.isRequired,
  fqdn: PropTypes.string.isRequired,
  zohoAuthResponse: PropTypes.shape({}),
};

Setup.defaultProps = {
  isActiveToolExist: false,
  toolName: '',
  iconUrl: '',
  fields: [],
  zohoAuthResponse: {},
};

export default React.memo(Setup);
